/**
 * Utility to resolve an included item (ID, Code, SKU, Title, or Object)
 * against the real MongoDB catalog tests array (/api/catalog/tests/).
 *
 * QA & Engineering Standard:
 * - Returns the ACTIVE matched catalog test object from MongoDB.
 * - If a test was DELETED from MongoDB catalog, returns `null` so callers can prune it.
 * - Ensures updated titles automatically sync everywhere.
 */

export function resolveCatalogTest(incItem, catalogTests = [], fallbackContext = {}) {
  if (!incItem) return null;

  const rawStr = (typeof incItem === 'string' ? incItem : (incItem?.title || incItem?.name || incItem?.code || incItem?._id || incItem?.id || '')).trim();
  const rawId = typeof incItem === 'object' ? (incItem?._id || incItem?.id || '') : incItem;
  const targetStrLower = rawStr.toLowerCase();
  const targetIdStr = String(rawId || '').toLowerCase();

  if (!targetStrLower && !targetIdStr) return null;

  // 1. Attempt exact match against catalogTests from MongoDB
  if (Array.isArray(catalogTests) && catalogTests.length > 0) {
    const matched = catalogTests.find(t => {
      if (!t) return false;
      const tId = String(t._id || t.id || '').toLowerCase();
      const tCode = String(t.code || t.sku || t.test_code || '').toLowerCase();
      const tTitle = String(t.title || t.name || '').toLowerCase();

      // Check ID match
      if (targetIdStr && (tId === targetIdStr || tId === targetStrLower)) return true;

      // Check Code / SKU match (e.g. "ywe", "cbc-01")
      if (tCode && targetStrLower && tCode === targetStrLower) return true;

      // Check Title / Name match
      if (tTitle && targetStrLower && tTitle === targetStrLower) return true;

      return false;
    });

    if (matched) {
      return matched;
    }

    // Secondary title match fallback (for slight title variations)
    const partialMatch = catalogTests.find(t => {
      const tTitle = String(t?.title || t?.name || '').toLowerCase();
      if (!tTitle || !targetStrLower) return false;
      return tTitle === targetStrLower || (tTitle.length > 4 && (tTitle.includes(targetStrLower) || targetStrLower.includes(tTitle)));
    });

    if (partialMatch) {
      return partialMatch;
    }

    // If catalogTests is fully loaded from MongoDB and test is not found -> it was DELETED from catalog!
    // Return null so callers filter it out completely (no ghost / phantom pills).
    return null;
  }

  // 2. If object already contains full test properties (e.g. populated from backend)
  if (typeof incItem === 'object' && (incItem.title || incItem.name) && (incItem.sample_matrix || incItem.specimen_type || incItem.short_description)) {
    return incItem;
  }

  // 3. Initial loading state (while catalog API call is in flight)
  // ponytail: temporary loading state placeholder before catalog list arrives
  const formattedTitle = rawStr
    ? rawStr.split(/[\s_-]+/).map(w => w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : '';

  if (!formattedTitle) return null;

  return {
    id: `inc-${rawStr}`,
    _id: `inc-${rawStr}`,
    title: formattedTitle,
    name: formattedTitle,
    code: (typeof incItem === 'string' && incItem.length <= 10) ? incItem.toUpperCase() : 'MUSB-TEST',
    sample_matrix: 'Whole Blood (EDTA)',
    collection_methods: ['Venipuncture (Standard Blood Draw)'],
    turnaround_time: '1-2 Business Days',
    fasting_required: false,
    short_description: `Comprehensive diagnostic testing for ${formattedTitle}. Evaluates key health markers.`,
    full_description: `This diagnostic panel evaluates key health markers for preventive care, wellness monitoring, and clinical analysis.`,
    included_biomarkers: [`${formattedTitle} Primary Marker`],
    category_names: fallbackContext?.category ? [fallbackContext.category] : ['Health Bundle'],
    price: parseFloat(fallbackContext?.price || 49),
    discount_price: parseFloat(fallbackContext?.discount_price || fallbackContext?.price || 49)
  };
}
